// tilakli-preview — w17 Tilakli wedding catalog card.
//
// Mood: eucalyptus + olive sage forest stationery, dewy and organic.
// The outer frame is a soft olive-gold gradient; the inner card sits
// on warm cream paper with a botanical leaf-circle ornament centred
// above the names. Category eyebrow in Uzbek: "TO'Y MAROSIMI".
import { Photo, type LayoutCtx } from "./_shared";

export function TilakliPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  // Olive-gold foil — warm earthy tones, slightly darker than Avgust.
  const FOIL =
    "linear-gradient(135deg, #7A5A22 0%, #B89042 22%, #DFC180 48%, #B89042 72%, #7A5A22 100%)";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
          position: "relative",
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TO&apos;Y MAROSIMI
        </div>

        {/* Eucalyptus leaf-circle ornament */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <svg
            viewBox="0 0 120 120"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            aria-hidden
          >
            <defs>
              <radialGradient id="til-leaf" cx="0.5" cy="0.4" r="0.7">
                <stop offset="0%" stopColor={accent} stopOpacity="0.6" />
                <stop offset="100%" stopColor={accent} stopOpacity="0.2" />
              </radialGradient>
            </defs>
            <circle cx="60" cy="60" r="56" fill="none" stroke={accent} strokeOpacity="0.45" strokeWidth="0.8" />
            <circle cx="60" cy="60" r="52" fill="none" stroke={accent} strokeOpacity="0.22" strokeWidth="0.4" />

            {/* Twelve leaves arranged around the centre */}
            {Array.from({ length: 12 }, (_, i) => (
              <g key={i} transform={`translate(60 60) rotate(${i * 30})`}>
                <ellipse cx="0" cy="-32" rx="3.4" ry="9" fill="url(#til-leaf)" stroke={accent} strokeOpacity="0.6" strokeWidth="0.5" />
                <line x1="0" y1="-22" x2="0" y2="-40" stroke={accent} strokeOpacity="0.7" strokeWidth="0.4" />
              </g>
            ))}

            {/* Inner secondary ring of smaller leaves */}
            {Array.from({ length: 8 }, (_, i) => (
              <g key={`b-${i}`} transform={`translate(60 60) rotate(${i * 45 + 22.5})`}>
                <ellipse cx="0" cy="-18" rx="2" ry="5" fill={accent} fillOpacity="0.55" />
              </g>
            ))}

            {/* Centre dot */}
            <circle cx="60" cy="60" r="2.8" fill={accent} fillOpacity="0.85" />
          </svg>

          {/* Optional inner photo */}
          {photo ? (
            <div
              style={{
                position: "absolute",
                inset: "28%",
                borderRadius: "50%",
                overflow: "hidden",
                boxShadow: `inset 0 0 0 1px ${accent}55`,
              }}
            >
              <Photo
                src={photo}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                fallback={
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: `radial-gradient(circle at 50% 45%, ${accent}33, ${bg})`,
                    }}
                  />
                }
              />
            </div>
          ) : null}
        </div>

        {/* Names */}
        <div
          style={{
            fontFamily: font,
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 400,
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Bottom caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TO&apos;Y MAROSIMI · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
